import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PORTFOLIO, PORTFOLIO_CATEGORIES } from "../data";

export const Portfolio = () => {
  const initialCat =
    (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("cat")) || "Alles";
  const [active, setActive] = useState(PORTFOLIO_CATEGORIES.includes(initialCat) ? initialCat : "Alles");

  const counts = useMemo(() => {
    const c = { Alles: PORTFOLIO.length };
    PORTFOLIO.forEach((p) => {
      c[p.category] = (c[p.category] || 0) + 1;
    });
    return c;
  }, []);

  const filtered = useMemo(
    () => (active === "Alles" ? PORTFOLIO : PORTFOLIO.filter((p) => p.category === active)),
    [active]
  );

  return (
    <section id="projecten" data-testid="portfolio-section" className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-40">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12">
        <div>
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px w-12 bg-[var(--turq)]" />
            <span className="font-mono-tech text-xs uppercase tracking-[0.25em] text-black/50">Projecten</span>
          </div>
          <h2 className="font-display text-4xl md:text-6xl font-medium tracking-tight leading-[0.98] max-w-2xl">
            Werk waar we<br />trots op zijn.
          </h2>
        </div>
        <p className="max-w-sm text-black/60 leading-relaxed">
          Een selectie uit ons eigen werk — echte projecten, van complete badkamers en keukens tot gevelrenovatie en verfijnde afwerking.
        </p>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 md:gap-3 mb-12 border-b border-black/10 pb-8" data-testid="portfolio-filters">
        {PORTFOLIO_CATEGORIES.map((cat) => {
          const isActive = active === cat;
          const count = counts[cat] || 0;
          return (
            <button
              key={cat}
              onClick={() => setActive(cat)}
              data-testid={`filter-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              aria-pressed={isActive}
              className={`group inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium border transition-[background-color,border-color,color,transform] duration-300 hover:-translate-y-0.5 ${
                isActive
                  ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                  : "bg-transparent text-black/70 border-black/15 hover:border-black/40"
              }`}
            >
              {cat}
              <span
                className={`font-mono-tech text-[10px] ${isActive ? "text-[var(--turq)]" : "text-black/35"}`}
              >
                {String(count).padStart(2, "0")}
              </span>
            </button>
          );
        })}
      </div>

      {/* Masonry gallery */}
      <motion.div layout className="columns-1 sm:columns-2 lg:columns-3 gap-6 md:gap-8">
        <AnimatePresence mode="popLayout">
          {filtered.map((p) => (
            <motion.figure
              key={p.img}
              layout
              data-testid={`project-${p.img.replace(/[^0-9]/g, "")}`}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="group relative mb-6 md:mb-8 break-inside-avoid overflow-hidden border border-black/10"
            >
              <img
                src={p.img}
                alt={p.title}
                loading="lazy"
                className="w-full h-auto object-cover grayscale-[35%] group-hover:grayscale-0 scale-100 group-hover:scale-[1.04] transition-[filter,transform] duration-[900ms] ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <figcaption className="absolute bottom-0 left-0 right-0 p-6 md:p-7 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-[transform,opacity] duration-500">
                <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[var(--turq)] mb-1.5">{p.cat}</p>
                <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight text-white leading-tight">{p.title}</h3>
              </figcaption>
            </motion.figure>
          ))}
        </AnimatePresence>
      </motion.div>
    </section>
  );
};
